import { client as apolloClient } from 'src/shared/graphql';
import {
  createAiOriginalImageCacheUpdater,
  createAiOriginalImagesCacheUpdater,
  uploadAiOriginalImageCacheUpdater,
  uploadAiOriginalImagesCacheUpdater,
} from 'src/shared/graphql/cache';
import type { NewProjectArgs } from 'src/shared/graphql/generated';
import { GetProjectDocument } from 'src/shared/graphql/generated';
import { mutations } from 'src/shared/graphql/requests';
import { logError } from 'src/shared/telemetry/logger';
import { createMd5Hash, uploadFileDirectly } from 'src/shared/utils/fileUpload';

import type { HomeownerAppTrackingData } from '../providers/HomeownerAppProvider';

import { initializeTracker } from './imageUploadTracker';

interface CreateAiOriginalImageConfig {
  spaceType?: string;
  email?: string;
  marketingConsent?: string;
  jobClientIdentifier?: string | null;
  project?: NewProjectArgs;
  projectId?: number;
}

interface UploadAiOriginalImageParams extends CreateAiOriginalImageConfig {
  file: File;
  trackingData: HomeownerAppTrackingData;
}

const uploadToStorageService = async ({
  file,
  email,
  spaceType,
  trackingData,
  project,
  projectId,
}: UploadAiOriginalImageParams) => {
  const checksum = await createMd5Hash(file);
  const createPlaceholderVariables = {
    byteSize: file.size,
    filename: file.name,
    contentType: file.type,
    checksum,
    email,
    spaceType,
  };
  let uploadUrl: string;
  let uploadHeaders: string | null | undefined;
  let imageSignedId: string;
  let aiOriginalImageId: number | null | undefined;

  const trackImageUploadStep = initializeTracker({
    ...trackingData,
    file_size_mb: file.size / 1000000,
    file_type: file.type,
  });

  try {
    const { data: createPlaceholderData, errors: createPlaceholderErrors } =
      await mutations.createAiOriginalImagePlaceholder(
        apolloClient,
        createPlaceholderVariables,
      );
    if (createPlaceholderErrors?.length) {
      createPlaceholderErrors.forEach(error => {
        logError(error);
      });
      return null;
    }
    uploadUrl =
      createPlaceholderData?.createAiOriginalImagePlaceholder.uploadUrl ?? '';
    uploadHeaders =
      createPlaceholderData?.createAiOriginalImagePlaceholder.uploadHeaders;
    imageSignedId =
      createPlaceholderData?.createAiOriginalImagePlaceholder.signedId ?? '';
    aiOriginalImageId =
      createPlaceholderData?.createAiOriginalImagePlaceholder.aiOriginalImageId;
    trackImageUploadStep(aiOriginalImageId || undefined);
  } catch (error) {
    logError(error as Error);
    throw error;
  }

  const updateImages = createAiOriginalImagesCacheUpdater();
  const updateImage = createAiOriginalImageCacheUpdater({
    id: String(aiOriginalImageId),
  });
  try {
    await uploadFileDirectly({
      file,
      uploadUrl,
      uploadHeaders: uploadHeaders ?? undefined,
      stripContentType: true,
      withRetry: true,
    });
    trackImageUploadStep();
    const refetchQueries = [];
    if (projectId) {
      refetchQueries.push({
        query: GetProjectDocument,
        variables: { projectId },
      });
    }
    const {
      errors: createAiOriginalImageErrors,
      data: createAiOriginalImageData,
    } = await mutations.createAiOriginalImage(
      apolloClient,
      {
        aiOriginalImageId: String(aiOriginalImageId),
        imageSignedId,
        agreedToTerms: true,
        imageFlexibility: 0,
        intensity: 0,
        style: '',
        project,
        projectId,
      },
      (cache, result) => {
        updateImage(cache, result);
        updateImages(cache, result);
      },
      refetchQueries,
    );
    if (createAiOriginalImageErrors?.length) {
      createAiOriginalImageErrors?.forEach(error => {
        logError(error);
      });
      return null;
    }
    trackImageUploadStep();
    return createAiOriginalImageData?.createAiOriginalImage;
  } catch (error) {
    logError(error as Error);
    throw error;
  }
};

const uploadDirectlyToHyperion = async ({
  file,
  email,
  spaceType,
  trackingData,
  marketingConsent,
  jobClientIdentifier,
  project,
  projectId,
}: UploadAiOriginalImageParams) => {
  const trackImageUploadStep = initializeTracker({
    ...trackingData,
    file_size_mb: file.size / 1000000,
    file_type: file.type,
  });
  trackImageUploadStep();
  const updateImagesFromUploadToHyperion = uploadAiOriginalImagesCacheUpdater();
  const updateImageFromUploadToHyperion = uploadAiOriginalImageCacheUpdater();
  const refetchQueries = [];
  if (projectId) {
    refetchQueries.push({
      query: GetProjectDocument,
      variables: { projectId },
    });
  }

  const { data } = await mutations.uploadAiOriginalImage(
    apolloClient,
    {
      image: file,
      email,
      spaceType,
      marketingConsent,
      jobClientIdentifier,
      project,
      projectId,
    },
    (cache, result) => {
      updateImagesFromUploadToHyperion(cache, result);
      updateImageFromUploadToHyperion(cache, result);
    },
    refetchQueries,
  );
  trackImageUploadStep();

  return data?.uploadAiOriginalImage;
};

const createAiOriginalImage = async (
  file: File,
  uploadConfig: CreateAiOriginalImageConfig,
  trackingData: HomeownerAppTrackingData,
  uploadToHyperion: boolean = false,
  jobClientIdentifier?: string | null,
) => {
  const {
    email = '',
    spaceType = '',
    marketingConsent,
    project,
    projectId,
  } = uploadConfig;
  if (uploadToHyperion) {
    return uploadDirectlyToHyperion({
      file,
      email,
      spaceType,
      trackingData,
      marketingConsent,
      jobClientIdentifier,
      project,
      projectId,
    });
  }

  return uploadToStorageService({
    file,
    email,
    spaceType,
    trackingData,
    project,
    projectId,
  });
};

export { createAiOriginalImage };
